import Analytics from '@/utils/analytics';
import i18n from '@/utils/i18n';
import React, { useMemo } from 'react';
import { BaseActionSheet } from '@/components/base-action-sheet';
import { E164_FORMAT } from '@/utils/phone-number';
import { List } from '@/components/list';
import { ListOption } from '@/components/list-item';
import { PhoneNumber } from 'libphonenumber-js';
import { StyleSheet, Text } from 'react-native';
import { TYPOGRAPHY } from '@/styles';

export interface PhoneNumberMetadataType {
  label?: string;
  number: PhoneNumber;
}

type ContactListOption = ListOption<PhoneNumberMetadataType>;

export interface ContactMetadataType {
  email: string | null;
  name: string;
  phoneNumbers: PhoneNumberMetadataType[];
}

interface Props {
  contact: ContactMetadataType | null;
  onClose: () => void;
  onSelect: (phoneNumber: string) => void;
}

export const ContactPhoneNumbersActionSheet: React.FC<Props> = ({
  contact,
  onClose,
  onSelect,
}) => {
  const data = useMemo(() => {
    return (contact?.phoneNumbers || []).reduce<ContactListOption[]>(
      (result, phoneNumber) => {
        const formattedPhoneNumber = phoneNumber.number.format(E164_FORMAT);
        result.push({
          metadata: phoneNumber,
          title: phoneNumber.number.formatNational(),
          value: formattedPhoneNumber,
        });
        return result;
      },
      [],
    );
  }, [contact]);

  return (
    <BaseActionSheet
      contentStyle={styles.base}
      isVisible={Boolean(contact)}
      onClose={onClose}
    >
      <Text style={[TYPOGRAPHY.actionSheetMessageText, styles.messageText]}>
        {i18n.t('select_contact_phone_number')}
      </Text>
      <List<ContactListOption>
        data={data}
        onSelect={({ value }) => {
          Analytics.track('contact_phone_numbers_action_sheet.contact.select');

          onSelect(String(value));
        }}
        renderRightComponent={({ metadata }) => (
          <Text style={[TYPOGRAPHY.listSmall, styles.labelText]}>
            {metadata?.label}
          </Text>
        )}
      />
    </BaseActionSheet>
  );
};

const styles = StyleSheet.create({
  base: {
    paddingHorizontal: 0,
  },
  labelText: {
    textTransform: 'capitalize',
  },
  messageText: {
    marginBottom: 12,
  },
});
